import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContentOnboardingState, fetchOnboardingStatus } from './hooks/useOnboardingState';
import ContentProgressBar from './components/ContentProgressBar';
import Step1SocialChannels from './components/Step1SocialChannels';
import Step2BrandCommunication from './components/Step2BrandCommunication';
import Step3ContentPlanning from './components/Step3ContentPlanning';
import logo from '../../assets/new-logo.png';

if (typeof window !== 'undefined') {
  window.onerror = function (message, source, lineno, colno, error) {
    const errorDiv = document.createElement('div');
    errorDiv.style.position = 'fixed';
    errorDiv.style.inset = '0';
    errorDiv.style.background = 'white';
    errorDiv.style.color = 'red';
    errorDiv.style.padding = '20px';
    errorDiv.style.fontFamily = 'monospace';
    errorDiv.style.zIndex = '999999';
    errorDiv.style.overflow = 'auto';
    errorDiv.innerHTML = `
      <div style="max-width: 800px; margin: 0 auto;">
        <h1 style="color: #e11d48; font-size: 24px; margin-bottom: 10px;">Diagnostic: React Runtime Error</h1>
        <p><strong>Message:</strong> ${message}</p>
        <p><strong>Source:</strong> ${source}</p>
        <p><strong>Line:</strong> ${lineno}:${colno}</p>
        <pre style="background: #f8fafc; border: 1px solid #e2e8f0; padding: 15px; border-radius: 8px; margin-top: 15px; white-space: pre-wrap; font-size: 13px;">${error?.stack || 'No stack trace available'}</pre>
      </div>
    `;
    document.body.appendChild(errorDiv);
    return false;
  };
}

export default function ContentOnboarding() {
  const navigate = useNavigate();
  const state = useContentOnboardingState();

  useEffect(() => {
    if (state.isComplete) {
      const timer = setTimeout(() => {
        navigate('/social-media-manager');
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [state.isComplete, navigate]);

  // The error banner sits above the step while Continue is at the bottom —
  // bring it into view whenever a new error appears.
  const errorBannerRef = useRef<HTMLDivElement>(null);
  const bannerError = state.stepError || state.fieldErrors.platforms;
  useEffect(() => {
    if (bannerError) {
      errorBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [bannerError]);

  if (state.isStatusLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-gray-500">Loading your setup...</p>
        </div>
      </div>
    );
  }

  if (state.loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-red-500 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-1">Couldn't load your setup</h2>
          <p className="text-sm text-gray-500 mb-6">{state.loadError}</p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={state.retryLoad}
              className="px-6 py-2.5 rounded-lg text-sm font-medium bg-teal-500 text-white hover:bg-teal-600 shadow-sm cursor-pointer flex items-center gap-2"
            >
              <i className="ri-refresh-line"></i>
              Retry
            </button>
            <button
              onClick={() => navigate('/marketing-dashboard')}
              className="px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            >
              Exit Setup
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state.isComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-double-line text-teal-600 text-3xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Onboarding Complete</h2>
          <p className="text-gray-500 text-sm mb-6">Your Content Manager is being configured with your preferences</p>
          <div className="w-48 h-1 bg-gray-200 rounded-full mx-auto overflow-hidden">
            <div className="h-full bg-teal-500 rounded-full animate-[loading_1s_ease-in-out_infinite]" style={{ width: '60%' }}></div>
          </div>
          <p className="text-xs text-gray-400 mt-3">Redirecting to Content Manager...</p>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (state.currentStep) {
      case 1:
        return (
          <Step1SocialChannels
            ecosystemUrl={state.ecosystemUrl}
            urlScanning={state.urlScanning}
            urlScanned={state.urlScanned}
            platforms={state.platforms}
            permissionsExpanded={state.permissionsExpanded}
            permissions={state.permissions}
            isStep1Valid={state.isStep1Valid}
            channelDetection={state.channelDetection}
            urlErrors={state.urlErrors}
            urlDirty={state.urlDirty}
            onEcosystemUrlChange={state.handleEcosystemUrlChange}
            onPlatformUrlChange={state.handlePlatformUrlChange}
            onPlatformUrlBlur={state.handlePlatformUrlBlur}
            onConnectPlatform={state.handleConnectPlatform}
            onDisconnectPlatform={state.handleDisconnectPlatform}
            onTogglePermissionsExpanded={() => state.setPermissionsExpanded(!state.permissionsExpanded)}
            onTogglePermission={state.togglePermission}
          />
        );
      case 2:
        return (
          <Step2BrandCommunication
            selectedTones={state.selectedTones}
            selectedArchetypes={state.selectedArchetypes}
            platformPriority={state.platformPriority}
            competitors={state.competitors}
            onToggleTone={state.toggleTone}
            onToggleArchetype={state.toggleArchetype}
            onMovePlatformPriority={state.movePlatformPriority}
            onUpdateCompetitor={state.updateCompetitor}
            onAddCompetitor={state.addCompetitor}
            onRemoveCompetitor={state.removeCompetitor}
            onBlurCompetitor={state.handleCompetitorBlur}
            isStep2Valid={state.isStep2Valid}
            fieldErrors={state.fieldErrors}
          />
        );
      case 3:
        return (
          <Step3ContentPlanning
            strategyMode={state.strategyMode}
            allocationPercent={state.allocationPercent}
            observanceEnabled={state.observanceEnabled}
            selectedObservances={state.selectedObservances}
            observanceError={state.observanceError}
            planCap={state.planCap}
            planName={state.planName}
            country={state.country}
            onSetCountry={state.setCountry}
            onSetSelectedObservances={state.setSelectedObservances}
            onSetStrategy={state.setStrategy}
            onSetAllocationPercent={state.setAllocationPercent}
            onSetObservanceEnabled={state.setObservanceEnabled}
            onToggleObservance={state.toggleObservance}
            onSelectAllObservances={state.selectAllObservances}
            maxObservances={state.maxObservances}
            onSetMaxObservances={state.setMaxObservances}
            onClearObservanceError={() => state.setObservanceError('')}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <img
            src={logo}
            alt="Marketing 4SIGHT Logo"
            className="h-9 w-auto object-contain flex-shrink-0"
          />
          <span className="text-gray-300">|</span>
          <span className="text-sm font-medium text-gray-600">Content Manager Setup</span>
        </div>
        <button
          onClick={() => navigate('/marketing-dashboard')}
          className="text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer flex items-center gap-1"
        >
          <i className="ri-close-line"></i>
          Exit Setup
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-10">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Social Media Manager Onboarding</h1>
            <p className="text-sm text-gray-500">Configure your AI-powered content distribution engine</p>
          </div>

          {/* Progress */}
          <ContentProgressBar currentStep={state.currentStep} />

          {/* Step Content */}
          {bannerError && (
            <div ref={errorBannerRef} className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2.5 animate-fade-in">
              <i className="ri-error-warning-line text-lg mt-0.5 shrink-0"></i>
              <div>
                <h4 className="font-bold">Unable to save step</h4>
                <p className="mt-0.5 text-xs text-red-600 font-medium">
                  {bannerError}
                </p>
              </div>
            </div>
          )}
          {renderStepContent()}

          {/* Navigation Footer */}
          <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200">
            {state.currentStep > 1 ? (
              <button
                onClick={state.handleBack}
                className="px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer flex items-center gap-2"
              >
                <i className="ri-arrow-left-line"></i>
                Back
              </button>
            ) : (
              <div></div>
            )}

            <div className="flex items-center gap-3">
              {state.draftSaveFailed && (
                <span
                  className="text-xs text-amber-600 flex items-center gap-1"
                  title="We couldn't autosave your latest changes. They're still on this page and will be saved when you continue."
                >
                  <i className="ri-error-warning-line"></i>
                  Draft not saved
                </span>
              )}
              <button
                onClick={state.handleContinue}
                disabled={
                  state.isSaving ||
                  (state.currentStep === 1 && !state.isStep1Valid) ||
                  (state.currentStep === 2 && !state.isStep2Valid)
                }
                className={`px-8 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                  state.isSaving ||
                  (state.currentStep === 1 && !state.isStep1Valid) ||
                  (state.currentStep === 2 && !state.isStep2Valid)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-teal-500 text-white hover:bg-teal-600 shadow-sm cursor-pointer'
                }`}
              >
                {state.isSaving ? (
                  <>
                    Saving...
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                  </>
                ) : state.currentStep === 3 ? (
                  <>
                    Launch Content Manager
                    <i className="ri-rocket-line"></i>
                  </>
                ) : (
                  <>
                    Save & Continue
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center text-xs text-gray-400 py-6">
          <p>&copy; {new Date().getFullYear()} Marketing 4SIGHT. All rights reserved.</p>
        </footer>
      </main>
    </div>
  );
}
